/**
 * @module config/currencyConfig
 * @private
 */
import { CURRENCY_ID } from '../constants';
import { Precision } from '../typings';

/**
 *
 * From right-to-left, after the decimal point, what is the grouping of number to insert separator?
 *
 * The default pattern is to group numbers by threes. For example, 500000000 --> 500,000,000
 *
 * In some countries, this is not always the case. For example, in India, it is repeating in a group of 3-2-2: 500000000 --> 50,00,00,000
 */
export const DEFAULT_SEPARATOR_PATTERN = [3] as const;
export const KRJP_SEPARATOR_PATTERN = [4] as const;
/**
 * @private
 */
const COMMA = ',';
/**
 * @private
 */
const PERIOD = '.';
/**
 * @private
 */
const SPACE = ' ';

export const SYMBOL_POSITION = {
  FRONT: 'FRONT',
  BACK: 'BACK',
  FRONT_AND_BACK: 'FRONT_AND_BACK',
} as const;

/**
 * @private
 * @since 2.0.0
 * @const {CurrencyConfig}
 */
const DEFAULT_CURRENCY_CONF = {
  symbol: '$',
  precision: 2,
  separator: COMMA,
  separatorPattern: DEFAULT_SEPARATOR_PATTERN,
  decimalPoint: PERIOD,
  spaceBetweenSymbol: false,
  symbolPosition: SYMBOL_POSITION.FRONT,
} as const;

type _BaseCurrencyConfig = {
  precision?: Precision;
  maxPrecision?: Precision;
  separator?: string | ((input: string) => string);
  separatorPattern?: Readonly<number[]>;
  separatorPatternCompact?: Readonly<number[]>;
  decimalPoint?: string;
};

export type CurrencyConfig = _BaseCurrencyConfig &
  (
    | {
        symbol: string;
        symbolPosition: 'BACK' | 'FRONT';
        spaceBetweenSymbol: boolean;
      }
    | {
        symbol: Readonly<[string, string]>;
        symbolPosition: 'FRONT_AND_BACK';
        spaceBetweenSymbol: Readonly<[boolean, boolean]>;
      }
  );

/**
 * Currency configurations.
 * @private
 * @since 1.0.1
 * @const {Object.<CurrencyConfig>}
 */
const CURRENCY: { [key: string]: CurrencyConfig } = {
  default: DEFAULT_CURRENCY_CONF,
  [CURRENCY_ID.SGD]: DEFAULT_CURRENCY_CONF,
  [CURRENCY_ID.IDR]: {
    symbol: 'Rp',
    precision: 0,
    separator: PERIOD,
    decimalPoint: COMMA,
    spaceBetweenSymbol: false,
    symbolPosition: SYMBOL_POSITION.FRONT,
  },
  [CURRENCY_ID.MYR]: {
    symbol: 'RM',
    precision: 2,
    separator: COMMA,
    decimalPoint: PERIOD,
    spaceBetweenSymbol: false,
    symbolPosition: SYMBOL_POSITION.FRONT,
  },
  [CURRENCY_ID.PHP]: {
    symbol: '₱',
    precision: 0,
    separator: COMMA,
    decimalPoint: PERIOD,
    spaceBetweenSymbol: false,
    symbolPosition: SYMBOL_POSITION.FRONT,
  },
  [CURRENCY_ID.TWD]: {
    symbol: '$',
    precision: 0,
    separator: COMMA,
    decimalPoint: PERIOD,
    spaceBetweenSymbol: false,
    symbolPosition: SYMBOL_POSITION.FRONT,
  },
  [CURRENCY_ID.THB]: {
    symbol: '฿',
    maxPrecision: 2,
    separator: COMMA,
    decimalPoint: PERIOD,
    spaceBetweenSymbol: false,
    symbolPosition: SYMBOL_POSITION.FRONT,
  },
  [CURRENCY_ID.VND]: {
    symbol: '₫',
    symbolPosition: SYMBOL_POSITION.BACK,
    precision: 0,
    separator: PERIOD,
    decimalPoint: COMMA,
    spaceBetweenSymbol: false,
  },
  [CURRENCY_ID.IRR]: {
    symbol: 'تومان',
    precision: 0,
    separator: COMMA,
    decimalPoint: PERIOD,
    spaceBetweenSymbol: false,
    symbolPosition: SYMBOL_POSITION.FRONT,
  },
  [CURRENCY_ID.HKD]: DEFAULT_CURRENCY_CONF,
  [CURRENCY_ID.BRL]: {
    symbol: 'R$',
    precision: 2,
    separator: PERIOD,
    decimalPoint: COMMA,
    spaceBetweenSymbol: false,
    symbolPosition: SYMBOL_POSITION.FRONT,
  },
  [CURRENCY_ID.CNY]: {
    symbol: '￥',
    precision: 2,
    separator: COMMA,
    decimalPoint: PERIOD,
    spaceBetweenSymbol: false,
    symbolPosition: SYMBOL_POSITION.FRONT,
  },
  [CURRENCY_ID.MXN]: {
    symbol: ['$', 'MXN'],
    spaceBetweenSymbol: [false, true],
    symbolPosition: SYMBOL_POSITION.FRONT_AND_BACK,
    precision: 2,
    separator: COMMA,
    decimalPoint: PERIOD,
  },
  [CURRENCY_ID.COP]: {
    symbol: 'COP',
    precision: 0,
    separator: PERIOD,
    decimalPoint: COMMA,
    spaceBetweenSymbol: false,
    symbolPosition: SYMBOL_POSITION.FRONT,
  },
  [CURRENCY_ID.CLP]: {
    symbol: '$',
    precision: 0,
    separator: PERIOD,
    decimalPoint: COMMA,
    spaceBetweenSymbol: false,
    symbolPosition: SYMBOL_POSITION.FRONT,
  },
  [CURRENCY_ID.ARS]: {
    symbol: '$',
    precision: 0,
    separator: PERIOD,
    decimalPoint: COMMA,
    spaceBetweenSymbol: false,
    symbolPosition: SYMBOL_POSITION.FRONT,
  },
  [CURRENCY_ID.PLN]: {
    symbol: 'zł',
    symbolPosition: SYMBOL_POSITION.BACK,
    precision: 2,
    separator: SPACE,
    decimalPoint: COMMA,
    spaceBetweenSymbol: false,
  },
  [CURRENCY_ID.EUR]: {
    symbol: '€',
    symbolPosition: SYMBOL_POSITION.BACK,
    precision: 2,
    separator: PERIOD,
    decimalPoint: COMMA,
    spaceBetweenSymbol: false,
  },
  [CURRENCY_ID.INR]: {
    symbol: '₹',
    precision: 0,
    separator: COMMA,
    decimalPoint: PERIOD,
    separatorPattern: [3, 2, 2],
    spaceBetweenSymbol: false,
    symbolPosition: SYMBOL_POSITION.FRONT,
  },
  [CURRENCY_ID.USD]: {
    symbol: 'USD',
    precision: 2,
    separator: COMMA,
    decimalPoint: PERIOD,
    spaceBetweenSymbol: false,
    symbolPosition: SYMBOL_POSITION.FRONT,
  },
  [CURRENCY_ID.KRW]: {
    symbol: '₩',
    precision: 0,
    separator: COMMA,
    decimalPoint: PERIOD,
    separatorPatternCompact: KRJP_SEPARATOR_PATTERN,
    spaceBetweenSymbol: false,
    symbolPosition: SYMBOL_POSITION.FRONT,
  },
  [CURRENCY_ID.JPY]: {
    symbol: '¥',
    precision: 0,
    separator: COMMA,
    decimalPoint: PERIOD,
    separatorPatternCompact: KRJP_SEPARATOR_PATTERN,
    spaceBetweenSymbol: false,
    symbolPosition: SYMBOL_POSITION.FRONT,
  },
  [CURRENCY_ID.KHR]: {
    symbol: '៛',
    precision: 0,
    separator: COMMA,
    decimalPoint: PERIOD,
    spaceBetweenSymbol: false,
    symbolPosition: SYMBOL_POSITION.BACK,
  },
  [CURRENCY_ID.MMK]: {
    symbol: 'Ks',
    precision: 0,
    separator: COMMA,
    decimalPoint: PERIOD,
    spaceBetweenSymbol: false,
    symbolPosition: SYMBOL_POSITION.FRONT,
  },
  [CURRENCY_ID.LAK]: {
    symbol: '₭',
    precision: 0,
    separator: COMMA,
    decimalPoint: PERIOD,
    spaceBetweenSymbol: false,
    symbolPosition: SYMBOL_POSITION.FRONT,
  },
} as const;

export default CURRENCY;
